import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import {
  Activity,
  BarChart3,
  BrainCircuit,
  CalendarCheck2,
  ChevronRight,
  HeartPulse,
  LineChart,
  Pill,
  Target,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { usePatients } from "@/lib/usePatients";
import { getRuntimeKnowledgeBase } from "@/lib/pharmacology/runtimeKnowledge";
import { buildBodyLoad } from "@/domain/pkBodyLoadEngine";
import { BodyLoadNowCard } from "@/components/clinical/BodyLoadNowCard";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";

const PATIENT_STORAGE_KEY = "clinicalos-my-space-patient";
const WINDOW_DAYS = 14;
const BODY_LOAD_DAYS = 120;

function dayKey(value: string | Date) {
  const d = new Date(value);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDate(value?: string | null) {
  if (!value) return "—";
  return new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }).format(new Date(value));
}

export default function InsightsHub() {
  const { data: patients = [] } = usePatients();
  const [patientId, setPatientId] = useState(() => localStorage.getItem(PATIENT_STORAGE_KEY) ?? "");
  const now = Date.now();
  const since = useMemo(() => new Date(now - WINDOW_DAYS * 24 * 3600_000).toISOString(), [now]);
  const bodyLoadSince = useMemo(() => new Date(now - BODY_LOAD_DAYS * 24 * 3600_000).toISOString(), [now]);
  const knowledge = useMemo(() => getRuntimeKnowledgeBase(), []);

  useEffect(() => {
    if (!patients.length) return;
    if (patients.some((p: any) => p.id === patientId)) return;
    const active = patients.find((p: any) => p.status === "ativo") ?? patients[0];
    setPatientId(active.id);
    localStorage.setItem(PATIENT_STORAGE_KEY, active.id);
  }, [patients, patientId]);

  const { data: doses = [] } = useQuery({
    queryKey: ["insights-doses", patientId, since],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("actual_time", since)
        .order("actual_time", { ascending: false })
        .limit(1500);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: bodyLoadDoses = [] } = useQuery({
    queryKey: ["insights-body-load", patientId, bodyLoadSince],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("medication_dose_logs")
        .select("*, substances(*), substance_formulations(*)")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("actual_time", bodyLoadSince)
        .order("actual_time", { ascending: false })
        .limit(1500);
      if (error) throw error;
      return data ?? [];
    },
    refetchInterval: 60_000,
  });

  const { data: checkins = [] } = useQuery({
    queryKey: ["insights-checkins", patientId, since],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("session_checkins")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null)
        .gte("checkin_at", since)
        .order("checkin_at", { ascending: false })
        .limit(500);
      if (error) throw error;
      return data ?? [];
    },
  });

  const { data: medications = [] } = useQuery({
    queryKey: ["insights-meds", patientId],
    enabled: !!patientId,
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from("patient_medications")
        .select("*")
        .eq("patient_id", patientId)
        .is("deleted_at", null);
      if (error) throw error;
      return data ?? [];
    },
  });

  const bodyLoad = useMemo(
    () => buildBodyLoad(bodyLoadDoses as any[], { now: new Date(now), knowledge }),
    [bodyLoadDoses, now, knowledge],
  );

  const usage = useMemo(() => {
    const days = new Set((doses as any[]).map((dose) => dayKey(dose.actual_time))).size;
    const count = doses.length;
    return {
      count,
      activeDays: days,
      perDay: days > 0 ? count / days : 0,
    };
  }, [doses]);

  const activeMeds = useMemo(
    () => (medications as any[]).filter((med) => !/suspens|descontinu|encerr/i.test(String(med.status ?? ""))),
    [medications],
  );

  const recentCheckin = checkins[0] as any | undefined;
  const dataDays = new Set([
    ...(doses as any[]).map((dose) => dayKey(dose.actual_time)),
    ...(checkins as any[]).map((item) => dayKey(item.checkin_at)),
  ]).size;

  function selectPatient(next: string) {
    setPatientId(next);
    localStorage.setItem(PATIENT_STORAGE_KEY, next);
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 pb-4">
      <header className="space-y-1 px-1 pt-1">
        <div className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">ClinicalOS</div>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Insights</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">Leitura longitudinal do que foi realmente registrado, sem transformar ausência de dados em conclusão.</p>
      </header>

      <Card className="rounded-3xl p-3">
        <label htmlFor="insights-patient" className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Perfil</label>
        <select
          id="insights-patient"
          className="mt-1 h-11 w-full rounded-xl border border-input bg-background px-3 text-base"
          value={patientId}
          onChange={(event) => selectPatient(event.target.value)}
        >
          <option value="">Selecione…</option>
          {patients.map((patient: any) => <option key={patient.id} value={patient.id}>{patient.social_name || patient.full_name}</option>)}
        </select>
      </Card>

      {patientId ? (
        <>
          <section className="grid gap-3 sm:grid-cols-2">
            <Link to="/doses" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-3xl">
              <Card className="h-full rounded-3xl p-4 hover:bg-muted/40">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-primary"><BarChart3 className="h-5 w-5" /><span className="font-semibold">Uso registrado</span></div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="mt-5 flex items-end justify-between gap-3">
                  <div><div className="text-3xl font-semibold tabular-nums">{usage.count}</div><div className="text-xs text-muted-foreground">registros em {WINDOW_DAYS} dias</div></div>
                  <div className="text-right"><div className="text-2xl font-semibold tabular-nums">{usage.perDay.toFixed(1)}</div><div className="text-xs text-muted-foreground">/dia ativo</div></div>
                </div>
                <div className="mt-4 flex h-14 items-end gap-1" aria-label={`${usage.activeDays} dias com dose registrada`}>
                  {Array.from({ length: 14 }, (_, index) => {
                    const date = new Date(now - (13 - index) * 24 * 3600_000);
                    const count = (doses as any[]).filter((dose) => dayKey(dose.actual_time) === dayKey(date)).length;
                    return <div key={index} className="min-w-0 flex-1 rounded-t bg-primary/70" style={{ height: `${Math.max(4, Math.min(100, count * 22))}%` }} title={`${count} registro(s)`} />;
                  })}
                </div>
              </Card>
            </Link>

            <Link to="/medicamentos" className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-3xl">
              <Card className="h-full rounded-3xl p-4 hover:bg-muted/40">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 text-primary"><CalendarCheck2 className="h-5 w-5" /><span className="font-semibold">Tratamento e adesão</span></div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="mt-5 text-3xl font-semibold tabular-nums">{activeMeds.length}</div>
                <div className="text-xs text-muted-foreground">item(ns) ativos no esquema</div>
                <div className="mt-4 rounded-2xl bg-muted/45 p-3 text-xs leading-relaxed text-muted-foreground">
                  O sistema não mostra percentual de adesão sem horários prescritos e registros suficientes; evita uma precisão falsa.
                </div>
              </Card>
            </Link>
          </section>

          <BodyLoadNowCard result={bodyLoad} historyWindowDays={BODY_LOAD_DAYS} />

          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <InsightLink to="/meu-espaco" icon={HeartPulse} title="Estado e check-ins" value={`${checkins.length}`} detail={recentCheckin ? `último ${formatDate(recentCheckin.checkin_at)}` : "sem check-in no período"} />
            <InsightLink to="/sintomas" icon={Target} title="Resposta individual" value={`${dataDays} dias`} detail="com dose e/ou check-in no período" />
            <InsightLink to="/curvas" icon={LineChart} title="Curvas e sessões" value={`${usage.activeDays}`} detail="dias com ingestão registrada" />
            <InsightLink to="/inteligencia-clinica" icon={BrainCircuit} title="Inteligência clínica" value="Abrir" detail="hipóteses, redose, texto e manejo revisável" />
            <InsightLink to="/interacoes" icon={Activity} title="Sobreposição" value="Revisar" detail="interações e carga farmacológica combinada" />
            <InsightLink to="/medicamentos" icon={Pill} title="Tratamento" value={`${activeMeds.length}`} detail="medicação(ões) em esquema ativo" />
          </section>

          <Card className="rounded-3xl border-dashed p-4 text-xs leading-relaxed text-muted-foreground">
            Insights são derivados dos registros do perfil e dos parâmetros estruturados da base. Associação temporal não é automaticamente causalidade clínica.
          </Card>
        </>
      ) : <Card className="rounded-3xl p-6 text-center text-sm text-muted-foreground">Selecione um perfil para calcular os insights.</Card>}
    </div>
  );
}

function InsightLink({ to, icon: Icon, title, value, detail }: { to: string; icon: typeof Activity; title: string; value: string; detail: string }) {
  return (
    <Link to={to} className="focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-3xl">
      <Card className="h-full rounded-3xl p-4 hover:bg-muted/40">
        <div className="flex items-start justify-between gap-2">
          <div className="grid h-9 w-9 place-items-center rounded-2xl bg-primary/10 text-primary"><Icon className="h-4 w-4" /></div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
        <div className="mt-3 text-sm font-semibold">{title}</div>
        <div className="mt-2 text-2xl font-semibold tabular-nums">{value}</div>
        <div className="mt-1 text-xs leading-relaxed text-muted-foreground">{detail}</div>
      </Card>
    </Link>
  );
}
